import { CURRENCY_OPTIONS } from '@/constants/currency';
import type {
  SelectItemType,
  SelectSize,
} from '@/lib/base/forms/select/select';
import { Select } from '@/lib/base/forms/select/select';

const POPULAR_CURRENCY_CODES = ['USD', 'EUR', 'GBP'];

// Currency options as ComboBox items: popular currencies (USD, EUR, GBP) first,
// then the rest alphabetically by code. Built once at module scope —
// CURRENCY_OPTIONS is static. Each item carries the symbol as its leading icon,
// the code as the label, and the name as supporting text; `textValue` makes the
// item findable by BOTH code and name in the combobox filter.
const CURRENCY_ITEMS: SelectItemType[] = [...CURRENCY_OPTIONS]
  .sort((a, b) => {
    const ap = POPULAR_CURRENCY_CODES.indexOf(a.value);
    const bp = POPULAR_CURRENCY_CODES.indexOf(b.value);
    if (ap !== -1 && bp !== -1) {
      return ap - bp;
    }
    if (ap !== -1) {
      return -1;
    }
    if (bp !== -1) {
      return 1;
    }
    return a.value.localeCompare(b.value);
  })
  .map(c => ({
    id: c.value,
    label: c.value,
    supportingText: c.name,
    textValue: `${c.value} ${c.name}`,
    icon: (
      // w-8, not w-5: three-character symbols (CHF, HK$, MX$, NZ$, lei) are
      // wider than 20px, and a centred overflow bleeds out of the box on both
      // sides and collides with the currency code beside it. A fixed width
      // (rather than min-w) also keeps every code aligned in one column.
      <span aria-hidden className='w-8 shrink-0 text-center text-tertiary'>
        {c.symbol}
      </span>
    ),
  }));

export interface CurrencyPickerControlProps {
  value: string;
  onChange: (code: string) => void;
  /** Accessible name for the combobox input (used when there's no visible label). */
  ariaLabel: string;
  /** Optional visible field label rendered above the input. */
  label?: string;
  hint?: string;
  isDisabled?: boolean;
  size?: SelectSize;
  placeholder?: string;
}

/**
 * Searchable currency selector built on the shared `Select.ComboBox` primitive.
 *
 * Using the library ComboBox (rather than a hand-rolled DialogTrigger + Popover)
 * is what lets this coordinate with the modal overlay stack: an option-click
 * inside the library popover is recognised as an in-overlay interaction, so
 * selecting a currency inside a dismissable modal (e.g. the CRM create/edit
 * form) no longer closes the modal. Options are sourced from CURRENCY_OPTIONS,
 * so the full currency list is always available.
 */
export function CurrencyPickerControl({
  value,
  onChange,
  ariaLabel,
  label,
  hint,
  isDisabled = false,
  size = 'sm',
  placeholder = 'Search currency',
}: CurrencyPickerControlProps) {
  return (
    <Select.ComboBox
      aria-label={ariaLabel}
      label={label}
      hint={hint}
      placeholder={placeholder}
      size={size}
      items={CURRENCY_ITEMS}
      selectedKey={value || null}
      onSelectionChange={key => {
        if (key !== null && key !== undefined) {
          onChange(String(key));
        }
      }}
      isDisabled={isDisabled}
      shortcut={false}
      // The ComboBox sizes its popover to the trigger, which is narrow in the
      // inline CRM edit (the amount field takes most of the row). At that width
      // the code and the currency name wrap onto two lines while the symbol
      // stays vertically centred, so it straddles both. min-width wins over the
      // inline trigger width, so this only ever widens the popover — a wider
      // trigger (the workspace settings field) is unaffected.
      popoverClassName='min-w-64'
      renderEmptyState={() => (
        <p className='px-3 py-2 text-sm text-tertiary'>No currencies found</p>
      )}
    >
      {(item: SelectItemType) => (
        <Select.Item
          id={item.id}
          label={item.label}
          supportingText={item.supportingText}
          textValue={item.textValue}
          icon={item.icon}
        />
      )}
    </Select.ComboBox>
  );
}
